import type { FacingMode } from '@fastgarage/protocol';

/**
 * Camera and microphone acquisition, kept apart on purpose.
 *
 * History: both used to be requested in a single getUserMedia call with a
 * relaxed "video only" attempt at the end of the fallback chain. That last
 * attempt was unreachable — a denied *microphone* raises NotAllowedError just
 * like a denied camera, and the chain aborts on NotAllowedError. Customers who
 * would happily have granted the camera ended up with nothing.
 *
 * Splitting the calls costs a second permission prompt and buys the guarantee
 * that no microphone problem can ever cost us the picture.
 */

/**
 * Which camera to open. `deviceId` addresses one exact video input (the only
 * way to reach an external USB/UVC camera, which announces no facing mode);
 * `facing` is the front/back fallback used when no deviceId is known.
 */
export interface CameraTarget {
  deviceId?: string;
  facing: FacingMode;
}

export interface CameraAcquisition {
  stream: MediaStream;
  videoTrack: MediaStreamTrack;
}

/** getUserMedia, injectable for tests. */
export type GetUserMedia = (constraints: MediaStreamConstraints) => Promise<MediaStream>;

/**
 * Raised when the browser exposes no getUserMedia at all: an insecure context
 * (plain http) or an in-app WebView. `navigator.mediaDevices` is then simply
 * undefined, and the bare property access would throw an unspecific TypeError.
 */
export class MediaUnavailableError extends Error {
  readonly name = 'MediaUnavailableError';
  constructor() {
    super('getUserMedia is unavailable in this context');
  }
}

/** The browser's getUserMedia bound to mediaDevices, or null when absent. */
export function browserGetUserMedia(): GetUserMedia | null {
  const devices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
  return typeof devices?.getUserMedia === 'function' ? devices.getUserMedia.bind(devices) : null;
}

function videoConstraints(target: CameraTarget, strict: boolean): MediaTrackConstraints {
  // With an exact deviceId the facing mode must be left out: USB cameras
  // report none, and an additional facingMode constraint can knock the
  // requested device out of the candidate set.
  const selector: MediaTrackConstraints = target.deviceId
    ? { deviceId: { exact: target.deviceId } }
    : { facingMode: { ideal: target.facing } };
  return strict
    ? {
        ...selector,
        width: { min: 1280, ideal: 1920 },
        height: { min: 720, ideal: 1080 },
        frameRate: { min: 24, ideal: 30 },
      }
    : {
        ...selector,
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 30 },
      };
}

/**
 * Opens the camera — video only. Tries the strict 1280x720@24 minimum with an
 * ideal of 1920x1080 first, then relaxes the constraints. External cameras
 * often support neither the resolution floor nor 24fps, so the relaxed attempt
 * is what usually carries them. A denied permission aborts immediately: with a
 * video-only request NotAllowedError can only mean the camera itself.
 */
export async function acquireCamera(
  target: CameraTarget | FacingMode,
  getUserMedia: GetUserMedia | null = browserGetUserMedia(),
): Promise<CameraAcquisition> {
  if (!getUserMedia) throw new MediaUnavailableError();
  const resolved: CameraTarget = typeof target === 'string' ? { facing: target } : target;
  const attempts: MediaStreamConstraints[] = [
    { video: videoConstraints(resolved, true), audio: false },
    { video: videoConstraints(resolved, false), audio: false },
  ];
  let lastError: unknown = new Error('getUserMedia failed');
  for (const constraints of attempts) {
    try {
      const stream = await getUserMedia(constraints);
      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        stream.getTracks().forEach((t) => t.stop());
        throw new Error('no video track in stream');
      }
      return { stream, videoTrack };
    } catch (err) {
      lastError = err;
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        throw err; // Camera denied — retrying is pointless.
      }
    }
  }
  throw lastError;
}

/**
 * Opens the microphone for the intercom. Best-effort by contract: a denied,
 * missing or occupied microphone yields null and the session continues without
 * sound. It must never throw — see the module comment.
 */
export async function acquireMicrophone(
  getUserMedia: GetUserMedia | null = browserGetUserMedia(),
): Promise<MediaStreamTrack | null> {
  if (!getUserMedia) return null;
  try {
    const stream = await getUserMedia({ audio: true, video: false });
    const track = stream.getAudioTracks()[0] ?? null;
    if (!track) stream.getTracks().forEach((t) => t.stop());
    return track;
  } catch {
    return null;
  }
}

/**
 * Plain-language message for a media failure, or null when `err` is not one —
 * every cause used to collapse into the same unspecific "Es ist ein Fehler
 * aufgetreten", which is exactly why field reports of "no prompt appears" were
 * impossible to tell apart.
 */
export function mediaErrorMessage(err: unknown): string | null {
  if (err instanceof MediaUnavailableError) {
    return (
      'Ihr Browser gibt die Kamera nicht frei. Das passiert, wenn die Seite in einer App ' +
      '(z. B. WhatsApp oder Facebook) geöffnet wurde. Bitte öffnen Sie den Link in Chrome ' +
      'oder Safari.'
    );
  }
  if (err instanceof TypeError) {
    // navigator.mediaDevices undefined — same cause as above, other path.
    return (
      'Ihr Browser gibt die Kamera nicht frei. Bitte öffnen Sie den Link direkt in Chrome ' +
      'oder Safari statt in einer App.'
    );
  }
  if (!(err instanceof DOMException)) return null;
  switch (err.name) {
    case 'NotAllowedError':
      return (
        'Ohne Kamerafreigabe ist keine Video-Schadenaufnahme möglich. Falls keine Abfrage ' +
        'erschienen ist, wurde die Kamera für diese Seite früher blockiert: tippen Sie auf ' +
        'das Schloss-Symbol in der Adressleiste, erlauben Sie die Kamera und laden Sie die ' +
        'Seite neu.'
      );
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'Es wurde keine passende Kamera gefunden. Bitte versuchen Sie es mit einem anderen Gerät.';
    case 'NotReadableError':
      return (
        'Die Kamera wird gerade von einer anderen App benutzt. Bitte schließen Sie diese ' +
        'App und versuchen Sie es erneut.'
      );
    case 'SecurityError':
      return 'Die Kamera ist in diesem Browser gesperrt. Bitte öffnen Sie den Link in Chrome oder Safari.';
    default:
      return null;
  }
}
